import { z } from 'zod';
import { ArchetypeSchema } from './archetype';
import { AspettoCategoriaSchema } from './aspetti';
import { ClassIdSchema, type ClassId } from './classId';
import { StartingEquipmentSchema } from './equipmentChoice';

/**
 * Dado di danno base della classe.
 *
 * Manuale base (riga 1812): "*d4, d6, d8 o d10*". Distribuzione per le 12
 * classi DWR:
 *  - d4: Mago
 *  - d6: Bardo, Chierico, Druido, Monaco, Sciamano, Warlock
 *  - d8: Ladro, Ranger
 *  - d10: Barbaro, Guerriero, Paladino
 *
 * Il `d12` compare nel manuale **solo come tetto** della progressione del
 * Druido in *Furia di Denti e Artigli* (mossa speciale), mai come dado di
 * danno base di una classe. Per questo l'enum si ferma a d10: una nuova
 * classe homebrew con d12 base richiederebbe un cambio di schema esplicito.
 */
export const DamageDieSchema = z.enum(['d4', 'd6', 'd8', 'd10']);
export type DamageDie = z.infer<typeof DamageDieSchema>;

/**
 * Suggerimenti di precetto di allineamento, specifici per la classe.
 *
 * Il manuale, sotto ogni allineamento, propone una "frase chiave" tipica di
 * quella classe (es. Barbaro Buono: "ti metti in pericolo per il bene di un
 * amico"). Questo oggetto contiene le 5 frasi suggerite.
 *
 * Tutti i campi sono opzionali per copertura difensiva: alcune classi
 * potrebbero non avere il suggerimento per ogni allineamento.
 *
 * Il giocatore può comunque scegliere il precetto dal manuale base
 * (manuale draft, sezione Allineamento) o scriverne uno custom.
 */
export const AlignmentPreceptSuggestionsSchema = z.object({
  buono: z.string().optional(),
  legale: z.string().optional(),
  neutrale: z.string().optional(),
  caotico: z.string().optional(),
  malvagio: z.string().optional(),
});
export type AlignmentPreceptSuggestions = z.infer<typeof AlignmentPreceptSuggestionsSchema>;

/**
 * Un gruppo di scelta sulle MOSSE iniziali.
 *
 * **Tutte e 12 le classi DWR** hanno almeno un gruppo di scelta sulle mosse
 * iniziali — non è una particolarità del Barbaro. Esempi:
 *
 *  - Barbaro: *Stili di Difesa* (1 di 2), *Istinti* (1 di 3, espandibile)
 *  - Bardo:   1 gruppo "scegli **2 di 4** mosse" → richiede `pickCount=2`
 *  - Chierico: 1 di 3 (Bilancia / Senti il Popolo Cantare? / Scacciare Non Morti)
 *  - Druido:  1 di 2 (Divinità della Natura / Mutaforma)
 *  - Guerriero: 1 di 2 (Parata / Tecniche Segrete di Combattimento)
 *  - Ladro:   1 di 3 (Avvelenatore / Speriamo che Tenga / Arte Persuasiva)
 *  - Mago:    1 di 2 (Scudo Magico / Scuola per Giovani Maghi)
 *  - Monaco:  1 Regola di 3 (Battaglia / Lavoro / Pietà)
 *  - Paladino: 1 di 2 (Ordine Sacro / Ordine Sovrannaturale)
 *  - Ranger:  1 di 2 (Marchio del Cacciatore / Compagno Animale)
 *  - Sciamano: 1 di 3 (Medium / Auspicio / Radici Profonde)
 *  - Warlock: 1 Vincolo di 3 (Catena / Tomo / Lama)
 *
 * Il flag `expandableInAdvanced` (Istinti del Barbaro) segnala che le opzioni
 * NON scelte all'inizio possono comunque essere prese al level-up nei pool
 * `advancedMoves2to5` / `advancedMoves6to10`.
 */
export const StartingMoveChoiceGroupSchema = z.object({
  /** ID stabile entro la classe. Es. 'stili-di-difesa', 'istinti'. */
  id: z.string().min(1).max(50),
  /** Nome leggibile (es. 'Stile di Difesa', 'Istinto'). */
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  /** ID delle mosse selezionabili nel gruppo (almeno 2). */
  moveOptions: z.array(z.string().min(1)).min(2),
  /**
   * Quante opzioni il giocatore deve scegliere alla creazione.
   * Default 1, valido per 11 classi su 12. Eccezione: il **Bardo** richiede
   * `pickCount: 2` (sceglie 2 mosse iniziali su 4).
   */
  pickCount: z.number().int().min(1).default(1),
  /**
   * Se true, le opzioni RIMANENTI possono essere prese come mossa avanzata
   * durante il level-up (vale per gli Istinti del Barbaro).
   */
  expandableInAdvanced: z.boolean().default(false),
});
export type StartingMoveChoiceGroup = z.infer<typeof StartingMoveChoiceGroupSchema>;

/**
 * Configurazione del sistema di lancio incantesimi per le classi casteggianti.
 *
 * **Classi casteggianti** (5): Mago, Chierico, Druido, Warlock, Paladino.
 * **Classi non casteggianti** (7): Barbaro, Bardo, Guerriero, Ladro, Monaco,
 * Ranger, Sciamano.
 *
 * Lo Sciamano usa una mistica diversa (Aldilà, spiriti, costi narrativi) ma
 * NON ha *Lanciare Incantesimi* / *Mana* / Tradizioni Sacre. Quindi `spellcasting`
 * resta `undefined` anche per lui.
 *
 * Riferimento: `docs/manual/SPELL_STRUCTURE.md`.
 */
export const SpellcastingConfigSchema = z.object({
  /**
   * Nome che la classe dà agli incantesimi di livello 0 (cantrip).
   * - Mago: 'Trucchetti'
   * - Chierico: 'Orazioni'
   * - Druido: 'Orazioni'
   * - Warlock: 'Suppliche'
   * - Paladino: stringa libera (l'etichetta esplicita non emerge dal manuale)
   */
  cantripLabel: z.string().min(1).max(50),
  /** ID della mossa "Lanciare Incantesimi *" (Arcani/Divini/del Patrono). */
  castingMoveId: z.string().min(1),
  /** ID della mossa "Mana" che genera la riserva. */
  manaMoveId: z.string().min(1),
  /**
   * ID delle Tradizioni Sacre selezionabili dalla classe.
   * - Chierico: tutte e 5 (Crepuscolo, Guarigione, Illuminazione, Negromanzia, Valore)
   * - Paladino: 3 (Negromanzia, Valore, Natura). NB: la Tradizione *Natura*
   *   è disponibile **solo se l'archetipo è Elfo** (regola di vincolo
   *   archetipo↔tradizione, attualmente NON enforced dallo schema —
   *   vedi `dev-workspace/docs/Future Improvements.md`).
   * - Druido: array vuoto (ha un'unica tradizione implicita "della Natura")
   * - Mago / Warlock: array vuoto (non usano Tradizioni)
   */
  tradizioni: z.array(z.string().min(1)).default([]),
});
export type SpellcastingConfig = z.infer<typeof SpellcastingConfigSchema>;

/**
 * Definizione completa di una classe del gioco.
 *
 * Ogni file in `src/data/classes/<id>.ts` esporta un oggetto che soddisfa
 * questo schema. Lo schema NON contiene dati di runtime (es. il PF attuale
 * di un PG): è SOLO il blueprint statico della classe come da manuale.
 *
 * Le mosse, gli archetipi e i template di equipaggiamento sono **riferimenti**
 * a oggetti più ricchi che vivono in cataloghi separati (`src/data/moves/`,
 * ecc.) per permettere il riuso (es. una mossa di base condivisa tra classi).
 */
export const ClassDefinitionSchema = z
  .object({
    /** ID stabile della classe (uno dei 12 valori di ClassId). */
    id: ClassIdSchema,
    /** Nome di display in italiano (es. 'Barbaro'). */
    name: z.string().min(1).max(50),

    /**
     * Citazione/voce introduttiva della classe (testo narrativo lungo).
     * Esempio: "Fissa bene questi codardi. Non capiscono chi sei…" (Barbaro).
     * Markdown ammesso.
     */
    intro: z.string().min(1),

    /** Lista di nomi suggeriti per il PG (manuale: sezione "Nomi"). */
    suggestedNames: z.array(z.string().min(1)).default([]),

    /**
     * Lista di titoli/epiteti suggeriti.
     * Manuale: solo il **Barbaro** ha questa sezione; per le altre classi
     * sarà array vuoto.
     */
    suggestedTitles: z.array(z.string().min(1)).default([]),

    /**
     * Categorie di Aspetto del PG (Occhi, Corpo, Vestiario, ecc.).
     * Tipicamente 3-4 per classe.
     */
    aspetti: z.array(AspettoCategoriaSchema).default([]),

    // === Statistiche di partenza ===

    /** Dado di danno base della classe (es. 'd10' per il Barbaro). */
    damageDie: DamageDieSchema,
    /**
     * Costante usata per calcolare PF massimi: `baseHp + COS_punteggio`.
     * Es. Barbaro = 8 → 8 + COS punteggio.
     */
    baseHp: z.number().int().positive(),
    /**
     * Costante usata per calcolare Carico massimo: `baseLoad + FOR_modificatore`.
     * Es. Barbaro = 10 → 10 + FOR mod.
     */
    baseLoad: z.number().int().min(0),

    /** Suggerimenti di precetto per ciascun allineamento (specifici della classe). */
    alignmentPrecepts: AlignmentPreceptSuggestionsSchema.default({}),

    // === Legami ===

    /**
     * Template di legami della classe. Il giocatore sceglie fino a `maxBonds`
     * di questi e li compila col nome di un compagno.
     */
    bondTemplates: z.array(z.string().min(1)).default([]),
    /**
     * Numero massimo di legami che il PG può mantenere attivi (varia 3-6).
     */
    maxBonds: z.number().int().positive().default(4),

    // === Archetipi ===

    /** Archetipi disponibili (3 per Barbaro, 5 per le altre 11 classi). */
    archetypes: z.array(ArchetypeSchema).min(1),

    // === Mosse ===

    /**
     * ID delle mosse iniziali AUTOMATICAMENTE concesse al PG di livello 1.
     * (Le mosse di scelta — Stile di Difesa, Istinto — vivono in
     * `startingChoiceGroups`.)
     */
    startingMoves: z.array(z.string().min(1)).default([]),
    /**
     * Gruppi di scelta sulle mosse iniziali (es. Stili di Difesa, Istinti
     * del Barbaro). Vuoto per la maggior parte delle classi.
     */
    startingChoiceGroups: z.array(StartingMoveChoiceGroupSchema).default([]),
    /** Pool di ID mosse avanzate disponibili al level-up nei livelli 2-5. */
    advancedMoves2to5: z.array(z.string().min(1)).default([]),
    /** Pool di ID mosse avanzate disponibili al level-up nei livelli 6-10. */
    advancedMoves6to10: z.array(z.string().min(1)).default([]),

    // === Incantesimi (solo classi casteggianti) ===

    /**
     * Configurazione lancio incantesimi.
     * Presente solo per le 5 classi casteggianti: Mago, Chierico, Druido,
     * Warlock, Paladino. (Lo Sciamano usa altre meccaniche e NON è qui.)
     */
    spellcasting: SpellcastingConfigSchema.optional(),

    // === Equipaggiamento iniziale ===

    startingEquipment: StartingEquipmentSchema.default({ always: [], choices: [] }),
  })
  /**
   * **FI-3** — invariante: il numero di template di legami coincide col
   * `maxBonds` della classe (manuale base, riga 1858: il PG ne sceglie fino
   * a `maxBonds`, e per tutte e 12 le classi DWR i template sono esattamente
   * `maxBonds`). Eccezione: `bondTemplates.length === 0` resta valido per
   * permettere stub di test e data-entry incrementale (placeholder che
   * esistono prima che le classi siano trascritte).
   */
  .refine((d) => d.bondTemplates.length === 0 || d.bondTemplates.length === d.maxBonds, {
    message: 'bondTemplates.length deve essere uguale a maxBonds (oppure 0 per stub/placeholder)',
    path: ['bondTemplates'],
  });
export type ClassDefinition = z.infer<typeof ClassDefinitionSchema>;

/**
 * Helper: type guard per verificare se una classe è casteggiante.
 */
export function isSpellcastingClass(
  def: ClassDefinition,
): def is ClassDefinition & { spellcasting: SpellcastingConfig } {
  return def.spellcasting !== undefined;
}

/**
 * Helper: ottiene il dado di danno per un dato `ClassId` da un catalogo.
 * Lancia se la classe non è nel catalogo.
 */
export function getClassDamageDie(
  catalog: Record<ClassId, ClassDefinition>,
  classId: ClassId,
): DamageDie {
  const def = catalog[classId];
  return def.damageDie;
}
